/**
 * Campi del giocatore. L'anagrafica (nome, cognome, nascita) la scrive e la
 * vede nei campi solo l'Admin; ruolo e piede sono di campo e li cambia anche
 * il Mister (V36). Il
 * nuovo giocatore (V41) li chiede tutti. I valori restano stringhe finche si
 * scrive: lo schema condiviso li ripulisce e trasforma i vuoti in null all'invio.
 */

import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ReactNode } from 'react';
import type { ValoriAnagrafica, ValoriCampo } from './anagrafica.js';
import { CampoData } from '../comuni/CampoData.js';
import { oggi } from '../comuni/date.js';
import { ETICHETTA_RUOLO_GIOCATORE, RUOLI_GIOCATORE } from '@calcio/shared';

/** Le voci di una tendina Radix non accettano il valore vuoto. */
const NESSUNO = 'nessuno';

export function CampiAnagrafica({
  valori,
  errori,
  onCambia,
}: {
  valori: ValoriAnagrafica;
  errori: Record<string, string>;
  onCambia: (valori: ValoriAnagrafica) => void;
}) {
  const campo = (nome: keyof ValoriAnagrafica, etichetta: string) => (
    <Field data-invalid={Boolean(errori[nome])}>
      <FieldLabel htmlFor={`giocatore-${nome}`}>{etichetta}</FieldLabel>
      <Input
        id={`giocatore-${nome}`}
        value={valori[nome]}
        aria-invalid={Boolean(errori[nome])}
        onChange={(e) => onCambia({ ...valori, [nome]: e.target.value })}
      />
      <FieldError>{errori[nome]}</FieldError>
    </Field>
  );

  return (
    <FieldGroup>
      <div className="grid gap-4 @lg:grid-cols-2">
        {campo('nome', 'Nome')}
        {campo('cognome', 'Cognome')}
      </div>
      <div className="grid gap-4 @lg:grid-cols-2">
        <Field data-invalid={Boolean(errori.dataNascita)}>
          <FieldLabel htmlFor="giocatore-dataNascita">Data di nascita</FieldLabel>
          <CampoData
            id="giocatore-dataNascita"
            valore={valori.dataNascita}
            max={oggi()}
            cancellabile
            invalido={Boolean(errori.dataNascita)}
            onCambia={(dataNascita) => onCambia({ ...valori, dataNascita })}
          />
          <FieldError>{errori.dataNascita}</FieldError>
        </Field>
      </div>
    </FieldGroup>
  );
}

/**
 * Griglia dei campi compatti: etichette allineate a destra in una colonna,
 * tendine nella colonna accanto; una coppia per riga, due quando c'e spazio.
 * La soglia si legge sul `@container` della card che la contiene: le due
 * coppie chiedono circa 550 pixel.
 */
export const GRIGLIA_COMPATTA =
  'mx-auto grid grid-cols-[auto_auto] items-center gap-3 @xl:grid-cols-[auto_auto_auto_auto]';

/**
 * Etichetta e tendina piccola sulla stessa riga, per le card della scheda:
 * dentro `GRIGLIA_COMPATTA` occupano due colonne, allineate con le altre.
 */
export function CampoCompatto({
  id,
  etichetta,
  children,
}: {
  id: string;
  etichetta: string;
  children: ReactNode;
}) {
  return (
    <Field orientation="horizontal" className="col-span-2 grid grid-cols-subgrid">
      {/* Il rientro stacca la seconda coppia dalla prima. Un nome lungo va a
          capo invece di spingere la tendina fuori dalla card. */}
      <FieldLabel htmlFor={id} className="justify-self-end text-right @xl:pl-6">
        {etichetta}
      </FieldLabel>
      {children}
    </Field>
  );
}

export function CampiCampo({
  valori,
  errori,
  onCambia,
  compatti = false,
}: {
  valori: ValoriCampo;
  errori: Record<string, string>;
  onCambia: (valori: ValoriCampo) => void;
  /** Nella card della scheda: tendine piccole, in riga con gli altri campi. */
  compatti?: boolean;
}) {
  const ruolo = (
    <Select
      value={valori.ruoloPreferito}
      onValueChange={(r) => onCambia({ ...valori, ruoloPreferito: r })}
    >
      <SelectTrigger
        id="giocatore-ruolo"
        size={compatti ? 'sm' : 'default'}
        className={compatti ? 'w-40' : 'w-full'}
        aria-invalid={Boolean(errori.ruoloPreferito)}
      >
        <SelectValue placeholder="Scegli il ruolo" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {RUOLI_GIOCATORE.map((ruolo) => (
            <SelectItem key={ruolo} value={ruolo}>
              {ETICHETTA_RUOLO_GIOCATORE[ruolo]}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
  const piede = (
    <Select
      value={valori.piede || NESSUNO}
      onValueChange={(p) => onCambia({ ...valori, piede: p === NESSUNO ? '' : p })}
    >
      <SelectTrigger
        id="giocatore-piede"
        size={compatti ? 'sm' : 'default'}
        className={compatti ? 'w-40' : 'w-full'}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value={NESSUNO}>Non indicato</SelectItem>
          <SelectItem value="destro">Destro</SelectItem>
          <SelectItem value="sinistro">Sinistro</SelectItem>
          <SelectItem value="ambidestro">Ambidestro</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );

  if (compatti) {
    return (
      <>
        <CampoCompatto id="giocatore-ruolo" etichetta="Ruolo">
          {ruolo}
        </CampoCompatto>
        <CampoCompatto id="giocatore-piede" etichetta="Piede">
          {piede}
        </CampoCompatto>
      </>
    );
  }

  return (
    <div className="grid gap-4 @lg:grid-cols-2">
      <Field data-invalid={Boolean(errori.ruoloPreferito)}>
        <FieldLabel htmlFor="giocatore-ruolo">Ruolo preferito</FieldLabel>
        {/* Nessuna voce vuota: il ruolo e obbligatorio. Il valore vuoto del
            nuovo giocatore mostra il segnaposto. */}
        {ruolo}
        <FieldError>{errori.ruoloPreferito}</FieldError>
      </Field>
      <Field>
        <FieldLabel htmlFor="giocatore-piede">Piede</FieldLabel>
        {piede}
      </Field>
    </div>
  );
}
